import React, { useState, useMemo, useCallback, memo } from "react";
import { getPodTemplates } from "@/services/api/agentic";
import type { PodTemplate } from "@/services/api/agentic/types";
import { Button } from "@/components/ui/button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import WorkerAvatar from "@/components/worker/WorkerAvatar";
import { getPodIconFromTemplate } from "@/utils/podUtils";
import { SearchBar, type SearchPill } from "@/components/ui/search";
import LoadingPlaceholder from "../ui/loading-placeholder";
import { usePodList } from "@/context/PodListContext";
import { DropDown } from "@/components/ui/dropdown";
import {
  faEllipsisVertical,
  faThumbtack,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { faThumbtack as faThumbtackSolid } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { useIsOsMode } from "@/components/ui/os-layout";
import clsx from "clsx";
import {
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";

// PodCard component for displaying individual pods
const PodCard = memo(function PodCard({
  pod,
  onSelect,
}: {
  pod: PodTemplate;
  onSelect: (pod: PodTemplate) => void;
}) {
  // Use both workers and worker_assignments to get the most accurate count
  // workers is an array of worker IDs (always present)
  // worker_assignments contains more detailed info but may not always be populated
  const workerCount = pod.workers?.length || pod.worker_assignments?.length || 0;
  const workersWithNames =
    pod.worker_assignments?.filter((worker) => worker.name) || [];

  const { pinnedPods, togglePodPin } = usePodList();
  const isPinned = pinnedPods.includes(pod._id);

  // Helper to show dropdown menu
  const renderDropdown = () => (
    <div
      className="flex items-center gap-2 z-10"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <DropDown
        label={
          <Button
            aria-label="Pod actions"
            tabIndex={0}
            color="color"
            icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
          >
            {""}
          </Button>
        }
        placement="bottom"
        width="w-32"
      >
        <div
          className="px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded cursor-pointer flex items-center gap-2"
          onClick={async (e) => {
            e.stopPropagation();
            try {
              await togglePodPin(pod._id);
            } catch (error) {
              console.error("Failed to toggle pod pin:", error);
            }
          }}
        >
          <FontAwesomeIcon
            icon={isPinned ? faThumbtackSolid : faThumbtack}
            className="w-3 h-3"
          />
          {isPinned ? "Unpin" : "Pin"}
        </div>
      </DropDown>
    </div>
  );

  return (
    <div
      data-testid="pod-directory-card"
      className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 flex flex-col h-full min-w-0 hover:shadow-lg transition-shadow cursor-pointer group relative"
      onClick={() => onSelect(pod)}
    >
      {/* Dropdown menu */}

      <div className="flex items-center gap-3 mb-4 min-w-0">
        <div className="shrink-0 bg-indigo-100 dark:bg-indigo-900/30 p-2 rounded-lg">
          <FontAwesomeIcon
            icon={getPodIconFromTemplate(pod)}
            className="text-xl text-indigo-600 dark:text-indigo-400"
          />
        </div>

        <div className="min-w-0 flex-1 -mt-1">
          <div className="font-medium flex min-w-0 items-center gap-2 text-slate-800 dark:text-slate-200">
            <div className="min-w-0 flex-1">
              <div className="truncate" title={pod.name}>
                {pod.name}
              </div>
            </div>
            <div className="shrink-0">{renderDropdown()}</div>
          </div>
          <div className="text-xs font-mono text-slate-500 dark:text-slate-400 flex gap-2 items-center">
            <div> {workerCount} Workers</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex-grow min-w-0">
          <div className="text-sm text-slate-500 dark:text-slate-400 truncate">
            {pod.description}
          </div>
        </div>
        <div className="flex -space-x-2">
          {workersWithNames.slice(0, 5).map((worker) => (
            <div
              key={worker.worker_id}
              className="w-6 h-6 rounded-full overflow-hidden border-2 border-white dark:border-slate-800"
            >
              <WorkerAvatar seed={worker.name} size={24} />
            </div>
          ))}
          {workerCount > 5 && (
            <div className="w-6 h-6 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center text-xs text-slate-600 dark:text-slate-400 border-2 border-white dark:border-slate-800">
              +{workerCount - 5}
            </div>
          )}
        </div>
      </div>

      {pod.objective && (
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 flex-grow">
          {pod.objective}
        </p>
      )}

      <div className="mt-auto">
        <Button
          color="indigo"
          className="w-full"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onSelect(pod);
          }}
        >
          View Pod
        </Button>
      </div>
    </div>
  );
});

export default function PodDirectory({
  onCreate,
  onSelectPod,
}: {
  onCreate?: () => void;
  onSelectPod?: (pod: PodTemplate) => void;
}) {
  const { pods, loading } = usePodList();
  const isOsMode = useIsOsMode();

  // Search and filter state
  const [pills, setPills] = useState<SearchPill[]>([]);

  // Use pods from context instead of local state
  const podTemplates = pods;

  // Filtering logic based on pills
  const filteredPods = useMemo(() => {
    return podTemplates.filter((pod) => {
      // Check search terms
      const searchPills = pills.filter((p) => p.type === "search");
      if (searchPills.length > 0) {
        const searchTerms = searchPills.map((p) => p.value.toLowerCase());
        const podText = `${pod.name} ${pod.description || ""} ${
          pod.objective || ""
        }`.toLowerCase();
        if (!searchTerms.every((term) => podText.includes(term))) {
          return false;
        }
      }

      return true;
    });
  }, [podTemplates, pills]);

  // Handlers
  const handleCreate = () => {
    if (onCreate) onCreate();
  };

  const handleSelectPod = (pod: PodTemplate) => {
    if (onSelectPod) onSelectPod(pod);
  };

  // Handle pills change from SearchBar
  const handlePillsChange = useCallback((newPills: SearchPill[]) => {
    setPills(newPills);
  }, []);

  return (
    <div className={isOsMode ? "p-4 h-full" : ""}>
      {isOsMode ? (
        <div className={clsx("h-full w-full rounded-2xl overflow-y-auto", WORKBENCH_SURFACE_CLASS, WORKBENCH_ROOT_TEXT_CLASS)}>
          <div className="p-4">
            {/* Search and Filter Section */}
            <div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="relative flex-grow">
                    <SearchBar
                      pills={pills}
                      onPillsChange={handlePillsChange}
                      placeholder="Search pods..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Pod Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {loading ? (
                <LoadingPlaceholder
                  pattern="card"
                  count={3}
                  className="col-span-full"
                />
              ) : filteredPods.length === 0 ? (
                <p className="col-span-full text-center text-slate-500 dark:text-slate-400">
                  No pods found.
                </p>
              ) : (
                filteredPods.map((pod) => (
                  <div key={pod._id} className="relative group">
                    <PodCard pod={pod} onSelect={handleSelectPod} />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Search and Filter Section */}
          <div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="relative flex-grow">
                  <SearchBar
                    pills={pills}
                    onPillsChange={handlePillsChange}
                    placeholder="Search pods..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Pod Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {loading ? (
              <LoadingPlaceholder
                pattern="card"
                count={3}
                className="col-span-full"
              />
            ) : filteredPods.length === 0 ? (
              <p className="col-span-full text-center text-slate-500 dark:text-slate-400">
                No pods found.
              </p>
            ) : (
              filteredPods.map((pod) => (
                <div key={pod._id} className="relative group">
                  <PodCard pod={pod} onSelect={handleSelectPod} />
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
